import {FORM_LOAD_DATA} from '~/frontend/src/constants';
import {
    StoreState,
    AppDispatch,
    Thunk
} from '~/frontend/src/store';
import {
    clearElusiveData,
    setElusiveData
} from '~/frontend/src/actions/elusiveData';
import dataLoadData from '~/frontend/src/actions/data/loadData';
import {
    LoadDataParameters,
    LoadDataResult
} from '~/shared/src/utilities/modules/form';
import {MergeMode} from '~/frontend/src/utilities/dataUtils';
import {Form} from '~/frontend/src/components/base/Form';

type Parameters = {
    [ key : string ] : any;
};

/**
 * @memberOf! Actions
 *
 * @param {string} panelKey
 * @param {string} dataContext
 * @param {Schema} schema
 * @param {Status} status
 * @param {string} viewKey
 * @param {MergeMode} [mergeMode='override']
 * @param {string} [configHash]
 * @param {number} [rootItemId]
 * @param {string} [rootItemType]
 * @param {string} [rootItemKey]
 * @param {string} [requestModule]
 * @param {string} [requestCommand]
 * @param {Parameters} [requestParams]
 * @param {( responseData : LoadDataResult ) => void} [successCallback]
 *
 * @return {Promise<LoadDataResult | false>}
 */
const loadData = ( panelKey : string, dataContext : string, schema : Schema, status : Status, viewKey : string, mergeMode : MergeMode = 'override', configHash? : string, rootItemId? : number, rootItemType? : string, rootItemKey? : string, requestModule : string = 'data', requestCommand : string = 'load', requestParams : Parameters | undefined = undefined, successCallback? : ( responseData : LoadDataResult ) => void ) : Thunk<Promise<LoadDataResult | false>> => async ( dispatch : AppDispatch, getState : () => StoreState ) : Promise<LoadDataResult | false> => {
    dispatch( {
        type: FORM_LOAD_DATA,
        panelKey,
        dataContext,
        schema,
        status,
        viewKey,
        mergeMode,
        configHash,
        rootItemId,
        rootItemType,
        rootItemKey,
        requestModule,
        requestCommand,
        requestParams
    } );

    let retVal : LoadDataResult | false = false,
        params : LoadDataParameters | undefined;

    if ( rootItemId ) {
        params = {
            schema,
            status,
            viewKey,
            rootItemId
        };
    }
    else if ( rootItemType && rootItemKey ) {
        params = {
            schema,
            status,
            viewKey,
            rootItemType,
            rootItemKey
        }
    }

    if ( params && configHash ) {
        params.configHash = configHash;
    }

    // clear global data only for non-new root item
    if ( ( rootItemId ?? 1 ) > 0 && global.data[ panelKey ] && global ) {
        global.data[ panelKey ] = {
            items     : {},
            references: {},
            schema
        };
    }

    dispatch( clearElusiveData( panelKey ) );

    const success = await dispatch( dataLoadData(
        `${requestModule}/${requestCommand}`,
        dataContext,
        panelKey,
        'loadData',
        schema,
        {
            params     : { ...params, ...requestParams },
            mergeMode,
            extraParams: [ 'configHash', 'dataStructure', 'dataDescriptors', 'layoutStructure', 'layoutElements', 'saveConfig' ]
        },
        true,
        ( data ) => {
            retVal = data as LoadDataResult;
            successCallback?.( data as LoadDataResult );
        }
    ) );

    if ( success ) {
        dispatch( setElusiveData( panelKey, {
            ...getState().elusiveData?.[ panelKey ],
            variables: {
                ...getState().elusiveData?.[ panelKey ]?.variables,
                lastSaveTimestamp: 0
            }
        } ) );
    }

    return retVal;
}
export type LoadData = typeof loadData;

export default loadData;
